/**
 * Summary View
 * Displays betting statistics summary at parlay or leg level
 */

import { StatCard } from '@components/ui';
import { AnalyticsFilterBar } from '@components/ui/AnalyticsFilterBar';
import type { AnalysisLevel, AnalyticsFilter } from '@parlay-picker/shared/analyticsScopes';
import type { LegStats } from '@utils/summaryCalculations';
import type { ReactElement } from 'react';

// =============================================================================
// Types
// =============================================================================

interface DateRange {
  from: Date;
  to: Date;
}

interface BigBet {
  amount: number;
  name?: string;
}

interface SummaryStats {
  totalWagered?: number;
  totalReturned?: number;
  netPL?: number;
  wins?: number;
  losses?: number;
  winRate?: string;
  roi?: string;
  biggestWin?: BigBet | null;
  biggestLoss?: BigBet | null;
  dateRange?: DateRange | null;
}

interface SummaryViewProps {
  stats: SummaryStats | null;
  legStats: LegStats;
  loading: boolean;
  filter: AnalyticsFilter;
  onFilterChange: (filter: AnalyticsFilter) => void;
  level: AnalysisLevel;
  onLevelChange: (level: AnalysisLevel) => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

// Safe number formatting - returns '0' for NaN/undefined/null
const safeFixed = (num: number | undefined | null, decimals: number = 0): string => {
  const n = Number(num);
  return Number.isNaN(n) ? '0' : n.toFixed(decimals);
};

// Format date range for display
const formatDateRange = (dateRange: DateRange | null | undefined): string | null => {
  if (!dateRange?.from || !dateRange?.to) return null;
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  const from = dateRange.from.toLocaleDateString('en-US', options);
  const to = dateRange.to.toLocaleDateString('en-US', options);
  // If same day, just show one date
  if (from === to) return from;
  return `${from} - ${to}`;
};

// =============================================================================
// Component
// =============================================================================

export function SummaryView({
  stats,
  legStats,
  loading,
  filter,
  onFilterChange,
  level,
  onLevelChange,
}: SummaryViewProps): ReactElement {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-theme-text-secondary">Loading betting history...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AnalyticsFilterBar
        showFilter
        filter={filter}
        onFilterChange={onFilterChange}
        showLevel
        level={level}
        onLevelChange={onLevelChange}
      />
      {level === 'legs' ? <LegsSummary legStats={legStats} /> : <ParlaysSummary stats={stats} />}
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function ParlaysSummary({ stats }: { stats: SummaryStats | null }): ReactElement {
  const {
    totalWagered = 0,
    totalReturned = 0,
    netPL = 0,
    wins = 0,
    losses = 0,
    winRate = '0',
    roi = '0',
    biggestWin = null,
    biggestLoss = null,
    dateRange = null,
  } = stats || {};

  const totalBets = wins + losses;
  const avgStake = totalBets > 0 ? totalWagered / totalBets : 0;
  const formattedDateRange = formatDateRange(dateRange);

  return (
    <>
      {formattedDateRange && (
        <div className="text-center text-sm text-theme-text-secondary">
          Based on bets from <span className="text-theme-text-secondary">{formattedDateRange}</span>
        </div>
      )}
      <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-4">
        <StatCard label="Total Wagered" value={`$${safeFixed(totalWagered)}`} />
        <StatCard label="Total Returned" value={`$${safeFixed(totalReturned)}`} />
        <StatCard
          label="Net P/L"
          value={`${netPL >= 0 ? '+' : ''}$${safeFixed(netPL)}`}
          color={netPL >= 0 ? 'text-theme-success' : 'text-theme-danger'}
        />
        <StatCard
          label="ROI"
          value={`${roi}%`}
          color={parseFloat(roi) >= 0 ? 'text-theme-success' : 'text-theme-danger'}
        />
      </div>
      <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-4">
        <StatCard label="Win Rate" value={`${winRate}%`} sub={`${wins}W - ${losses}L`} />
        <StatCard label="Avg Stake" value={`$${safeFixed(avgStake)}`} />
        <StatCard
          label="Biggest Win"
          value={biggestWin ? `$${safeFixed(biggestWin.amount)}` : '$0'}
          sub={biggestWin?.name || 'No wins yet'}
          color="text-theme-success"
        />
        <StatCard
          label="Biggest Loss"
          value={biggestLoss ? `$${safeFixed(biggestLoss.amount)}` : '$0'}
          sub={biggestLoss?.name || 'No losses yet'}
          color="text-theme-danger"
        />
      </div>
    </>
  );
}

function LegsSummary({ legStats }: { legStats: LegStats }): ReactElement {
  const {
    totalLegs,
    settledLegs,
    pendingLegs,
    totalHits,
    totalMisses,
    hitRate,
    overHitRate,
    underHitRate,
  } = legStats;

  return (
    <>
      <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-4">
        <StatCard label="Total Legs" value={String(totalLegs)} sub={`${settledLegs} settled`} />
        <StatCard
          label="Hit Rate"
          value={`${hitRate}%`}
          sub={`${totalHits} hits / ${settledLegs} settled`}
          color={Number(hitRate) >= 50 ? 'text-theme-success' : 'text-theme-danger'}
        />
        <StatCard
          label="Overs Hit Rate"
          value={`${overHitRate}%`}
          color={Number(overHitRate) >= 50 ? 'text-theme-success' : 'text-theme-danger'}
        />
        <StatCard
          label="Unders Hit Rate"
          value={`${underHitRate}%`}
          color={Number(underHitRate) >= 50 ? 'text-theme-success' : 'text-theme-danger'}
        />
      </div>
      <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-4">
        <StatCard label="Hits" value={String(totalHits)} color="text-theme-success" />
        <StatCard label="Misses" value={String(totalMisses)} color="text-theme-danger" />
        <StatCard label="Pending" value={String(pendingLegs)} color="text-theme-warning" />
        <StatCard label="Players Tracked" value={String(Object.keys(legStats.byPlayer).length)} />
      </div>
    </>
  );
}
